import React, { useContext, useEffect, useRef } from 'react';
import classes from './PostEditorHeader.module.scss';
import { CategoryProfilesSelector } from '../CategoryProfilesSelector';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { PostEditorContextEnum } from '../../../models/post-editor';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { useApolloClient } from '@apollo/client';
import {
  GetPostDocument,
  GetPostQuery,
  GetPostQueryVariables,
  GetPostsEditorDocument,
  GetPostsEditorQuery,
  GetPostsEditorQueryVariables
} from '../../../models/graphql';
import { AppContext } from '../../../providers/AppProvider';
import { TikTokProfileValues } from '../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { mapSocialProfilesWithBoardsToPostGroupProfiles } from '../../../helpers/mapSocialProfilesWithBoardsToPostGroupProfiles';
import classNames from 'classnames';

export type PostEditorHeaderViewProps = {
  //
  formInstance: UseFormReturn;
  postId?: string;
};

export const PostEditorHeaderView = ({ formInstance, postId }: PostEditorHeaderViewProps): JSX.Element => {
  const { postEditorContext } = useContext(AppContext);
  const { state, dispatch, profiles: socialProfiles, loading } = useContext(PostEditorContext);
  const { cache } = useApolloClient();
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const { profiles: postEditorStateProfiles, categoryId: selectedCategoryId, readOnlyPosts } = postGroup;

  const disableProfilesSelector = [
    PostEditorContextEnum.restore_trashed,
    PostEditorContextEnum.reschedule_new,
    PostEditorContextEnum.reschedule_edit
  ].includes(postEditorContext.type);

  const stringifiedPostEditorStateProfiles = JSON.stringify(postEditorStateProfiles, undefined, '');

  const watchCategories: string | undefined = useWatch({
    control: formInstance.control,
    name: `postGroup_${postGroupIndex}_categoryId`
  });
  const watchProfiles: Array<string | false> | undefined = useWatch({
    control: formInstance.control,
    name: `postGroup_${postGroupIndex}_profiles`
  });
  const watchPnBoards: Array<string | undefined> | undefined = useWatch({
    control: formInstance.control,
    name: `postGroup_${postGroupIndex}_profiles_pinterestBoard`
  });
  const watchTikToks: Array<TikTokProfileValues | undefined> | undefined = useWatch({
    control: formInstance.control,
    name: `postGroup_${postGroupIndex}_profiles_tikTok`
  });

  const profilesFormValueStringified = JSON.stringify(watchProfiles);
  const pnBoardsFormValueStringified = JSON.stringify(watchPnBoards);
  const tikTokSettingsFormValuesStringified = JSON.stringify(watchTikToks);
  const profilesStateValueStringifiedRef = useRef<string>(profilesFormValueStringified);
  const pnBoardsStateValueStringifiedRef = useRef<string>(pnBoardsFormValueStringified);
  const tikTokSettingsFormValuesStringifiedRef = useRef<string>(tikTokSettingsFormValuesStringified);

  useEffect(() => {
    if (watchCategories) {
      dispatch({ type: 'updateCategories', payload: { categoryId: watchCategories, postGroupIndex } });
    }
  }, [watchCategories, dispatch, postGroupIndex]);

  // Update profiles or boards or tikTokSettings if their values change.
  useEffect(() => {
    if (!profilesFormValueStringified || loading) return;
    const profilesStateValueStringified = profilesStateValueStringifiedRef.current;
    const pnBoardsStateValueStringified = pnBoardsStateValueStringifiedRef.current;
    const tikTokSettingsStateValueStringified = tikTokSettingsFormValuesStringifiedRef.current;
    const parsedProfiles: Array<string | false> = JSON.parse(profilesFormValueStringified);
    const parsedBoards: Array<string> = pnBoardsFormValueStringified ? JSON.parse(pnBoardsFormValueStringified) : [];
    const parsedTikTok: Array<TikTokProfileValues | null> = tikTokSettingsFormValuesStringified
      ? JSON.parse(tikTokSettingsFormValuesStringified)
      : [];

    // Only dispatch the update if the previous profiles aren't the same as the new ones. This is because the first
    // time the post editor loads, we set the form value for the profiles and don't want to update the state again
    // which would trigger validation.
    if (
      parsedProfiles.length &&
      (profilesStateValueStringified !== profilesFormValueStringified ||
        pnBoardsStateValueStringified !== pnBoardsFormValueStringified ||
        tikTokSettingsStateValueStringified !== tikTokSettingsFormValuesStringified)
    ) {
      profilesStateValueStringifiedRef.current = profilesFormValueStringified;
      pnBoardsStateValueStringifiedRef.current = pnBoardsFormValueStringified;
      tikTokSettingsFormValuesStringifiedRef.current = tikTokSettingsFormValuesStringified;
      const profiles: SelectedSocialProfileValue[] = mapSocialProfilesWithBoardsToPostGroupProfiles(
        socialProfiles,
        parsedProfiles,
        parsedBoards,
        parsedTikTok
      );
      dispatch({ type: 'updateProfiles', payload: { postGroupIndex, profiles } });
    }
  }, [
    socialProfiles,
    profilesFormValueStringified,
    pnBoardsFormValueStringified,
    tikTokSettingsFormValuesStringified,
    dispatch,
    loading,
    postGroupIndex
  ]);

  // this use effects is to check if we need to update customized profiles (adding / or removing profiles might trigger)
  // customization off.
  useEffect(() => {
    if (socialProfiles.length) {
      let siblings: GetPostsEditorQuery['latestFamilyMembersOfPost'] | null = null;
      if (postId) {
        const cachedFamily = cache.readQuery<GetPostsEditorQuery, GetPostsEditorQueryVariables>({
          query: GetPostsEditorDocument,
          variables: {
            postId
          }
        });
        const cachedPost = cache.readQuery<GetPostQuery, GetPostQueryVariables>({
          query: GetPostDocument,
          variables: {
            postId
          }
        });
        siblings =
          cachedFamily?.latestFamilyMembersOfPost || (cachedPost && cachedPost.post ? [cachedPost.post] : null);
      }
      dispatch({
        type: 'updateCustomizeProfiles',
        payload: {
          postGroupIndex,
          availableProfiles: socialProfiles,
          siblings,
          postEditorContext: postEditorContext
        }
      });
    }
  }, [socialProfiles, dispatch, stringifiedPostEditorStateProfiles, postGroupIndex, postId, cache, postEditorContext]);

  return (
    <div
      className={classNames(classes.PostEditorNewHeader, {
        [classes.PostEditorNewHeaderBulkImporter]: postEditorContext.type === PostEditorContextEnum.import
      })}>
      <CategoryProfilesSelector
        selectAllFormName={`postGroup_${postGroupIndex}_select_all_profiles`}
        categoriesFieldName={`postGroup_${postGroupIndex}_categoryId`}
        formInstance={formInstance}
        profilesFieldName={`postGroup_${postGroupIndex}_profiles`}
        disableProfilesSelector={disableProfilesSelector}
        {...(selectedCategoryId ? { selectedCategoryId } : {})}
        {...(postEditorStateProfiles.length ? { selectedProfiles: postEditorStateProfiles } : {})}
        {...((postEditorContext.type === PostEditorContextEnum.edit && readOnlyPosts.length) ||
        postEditorContext.type === PostEditorContextEnum.reschedule_edit
          ? { dontSetDefaultProfiles: true }
          : {})}
      />
    </div>
  );
};
